import { z } from "zod";
import {
  SmartCreationPreciseStructureAvailabilitySchema,
  SmartCreationStructureSchema,
} from "./draft";
import {
  isSmartCreationDraftCampaignSelected,
  SmartCreationBudgetAnalysisSchema,
  SmartCreationDraftContentV2Schema,
  SmartCreationStructureCountsSchema,
  SmartCreationStructureDiagnosticSchema,
  SmartCreationStructureRepairActionSchema,
} from "./draft-content";

const SmartCreationStructureEvaluationBaseSchema = z.object({
  structure: SmartCreationStructureSchema,
  counts: SmartCreationStructureCountsSchema,
  budget: SmartCreationBudgetAnalysisSchema,
  diagnostics: z.array(SmartCreationStructureDiagnosticSchema).max(100),
  repairActions: z.array(SmartCreationStructureRepairActionSchema).max(20),
});

export const SmartCreationStructureEvaluationSchema = z
  .discriminatedUnion("feasible", [
    SmartCreationStructureEvaluationBaseSchema.extend({
      feasible: z.literal(true),
      draftContent: SmartCreationDraftContentV2Schema,
    }).strict(),
    SmartCreationStructureEvaluationBaseSchema.extend({
      feasible: z.literal(false),
      draftContent: z.null(),
    }).strict(),
  ])
  .superRefine((evaluation, ctx) => {
    if (evaluation.feasible && evaluation.diagnostics.some((diagnostic) => diagnostic.blocking)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "A feasible Structure evaluation cannot contain blocking diagnostics",
        path: ["diagnostics"],
      });
    }
    if (!evaluation.feasible && !evaluation.diagnostics.some((diagnostic) => diagnostic.blocking)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "An infeasible Structure evaluation requires a blocking diagnostic",
        path: ["diagnostics"],
      });
    }
    if (evaluation.draftContent && evaluation.draftContent.structure !== evaluation.structure) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Evaluation and DraftContent Structures must match",
        path: ["draftContent", "structure"],
      });
    }
    if (evaluation.draftContent) {
      const summary = evaluation.draftContent.summary;
      for (const key of [
        "campaignCount",
        "adGroupCount",
        "productAdCount",
        "targetCount",
      ] as const) {
        if (evaluation.counts[key] !== summary[key]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${key} must match the generated draft summary`,
            path: ["counts", key],
          });
        }
      }
      if (evaluation.budget.currencyCode !== evaluation.draftContent.currencyCode) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Evaluation and draft currencies must match",
          path: ["budget", "currencyCode"],
        });
      }
      for (const key of ["totalDailyBudget", "minimumViableDailyBudget"] as const) {
        if (Math.abs(evaluation.budget[key] - summary[key]) > 1e-8) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${key} must match the generated draft summary`,
            path: ["budget", key],
          });
        }
      }

      const campaignBudgets = new Map(
        evaluation.draftContent.campaigns
          .filter(isSmartCreationDraftCampaignSelected)
          .map((campaign) => [campaign.itemKey, campaign.dailyBudget]),
      );
      const allocationKeys = evaluation.budget.allocations.map(
        (allocation) => allocation.campaignKey,
      );
      if (
        allocationKeys.length !== campaignBudgets.size ||
        new Set(allocationKeys).size !== allocationKeys.length
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Budget allocations must contain one unique entry per Campaign",
          path: ["budget", "allocations"],
        });
      }
      for (const [index, allocation] of evaluation.budget.allocations.entries()) {
        const campaignBudget = campaignBudgets.get(allocation.campaignKey);
        if (
          campaignBudget === undefined ||
          Math.abs(allocation.dailyBudget - campaignBudget) > 1e-8
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Budget allocation must match its generated Campaign",
            path: ["budget", "allocations", index],
          });
        }
      }
    }
  });
export type SmartCreationStructureEvaluation = z.infer<
  typeof SmartCreationStructureEvaluationSchema
>;

export const SmartCreationStructureRecommendationSchema = z
  .object({
    suggested: SmartCreationStructureSchema.nullable(),
    basis: z.enum(["observed_cpa", "platform_minimum", "none"]),
    observedCpa: z.number().finite().positive().nullable(),
    evidenceWindowDays: z.union([z.literal(14), z.literal(30), z.literal(60)]).nullable(),
    lowBudget: z.boolean(),
  })
  .strict();
export type SmartCreationStructureRecommendation = z.infer<
  typeof SmartCreationStructureRecommendationSchema
>;

export const SmartCreationStructureOptionsEvaluationSchema = z
  .object({
    standard: SmartCreationStructureEvaluationSchema,
    binary: SmartCreationStructureEvaluationSchema,
    precise: z.union([
      SmartCreationStructureEvaluationSchema,
      SmartCreationPreciseStructureAvailabilitySchema,
    ]),
    recommendation: SmartCreationStructureRecommendationSchema.optional(),
  })
  .strict()
  .superRefine((options, ctx) => {
    if (options.standard.structure !== "standard") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "The Standard option requires a Standard evaluation",
        path: ["standard", "structure"],
      });
    }
    if (options.precise.structure !== "precise")
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "The Precise option requires a Precise evaluation",
        path: ["precise", "structure"],
      });
    if (options.binary.structure !== "binary") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "The Binary option requires a Binary evaluation",
        path: ["binary", "structure"],
      });
    }
  });
export type SmartCreationStructureOptionsEvaluation = z.infer<
  typeof SmartCreationStructureOptionsEvaluationSchema
>;
